import { useNavigate } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import {
  createEntityAppointment,
  updateAppointmentAndRecurrence,
  updateAppointmentV2
} from 'api/Appointment';

import { deleteKeys, formatDate, getDirtyValues, iaRa } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';

import checkIfRecurrenceChanged from '../lib/checkIfRecurrenceChanged';

const useAppointmentUpdate = ({ appointment, handleClose, setAppointmentId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isRecurrence = appointment?.isRecurrence;
  const { mutateAsync: mutateAppointment } = useMutation({
    mutationFn: (data) => {
      const isReschedule = !moment(data.starts_at).isSame(moment(appointment.starts_at));

      const actionType = data?.actionType;
      const isCreateEntity = isRecurrence && actionType === 'only' && !data?.parent_appointment_id;
      //if the user makes changes to the recurring appointment and wants to apply changes only to this appointment the we create entity
      const dirtyValues = getDirtyValues(data, appointment);
      deleteKeys({
        object: dirtyValues,
        keys: [
          'tags',
          'calculate',
          'timeline',
          'timeline_bar',
          'isPaymentActive',
          'arrived_data',
          'actionType'
        ]
      });

      const { appointment_changes, isRecurrenceChanged } = checkIfRecurrenceChanged(
        dirtyValues,
        data,
        actionType === 'following'
      );

      let response = null;

      if (isCreateEntity) {
        response = createEntityAppointment(navigate, {
          appointment_id: data.id,
          appointment_date: {
            starts_at: data.starts_at,
            ends_at: data.ends_at,
            deleted_date: formatDate(data.starts_at)
          },
          appointment_changes,
          type: 'update'
        });

        return response;
      }

      if (!isReschedule && (isRecurrenceChanged || actionType === 'following')) {
        response = updateAppointmentAndRecurrence(navigate, {
          ...appointment_changes,
          id: data.id
        });

        return response;
      }

      return updateAppointmentV2(
        navigate,
        {
          ...appointment_changes,
          ...(data?.isRecurrence && { entity_changed: true }),
          id: data.id
        },
        data
      );
    },
    onSuccess: async (data) => {
      const { code, updatedAppointment, error, rescheduledAppointmentId, type } = data;

      if (rescheduledAppointmentId) {
        handleAppointmentAlreadyRescheduled(rescheduledAppointmentId);
      }

      const invalidateAfter1s = new Promise((resolve) => {
        setTimeout(() => {
          queryClient.invalidateQueries(['appointment-events']);
          queryClient.invalidateQueries(['appointmentv2', updatedAppointment?.id]);
          resolve();
        }, 1000);
      });

      await invalidateAfter1s;
      if (code === 0) {
        if (type === 'entity_create') {
          handleAppointmentEntityCreate(updatedAppointment);
          return;
        }

        if (type === 'recurrence_update') {
          handleClose();
          return;
        }

        if (type === 'recurrence_deleted') {
          setAppointmentId(updatedAppointment?.id);
          return;
        }

        if (appointment?.id === updatedAppointment?.id) {
          handleAppointmentUpdated(updatedAppointment);
        } else if (appointment?.id !== updatedAppointment?.id) {
          handleAppointmentUpdatedAndRescheduled(updatedAppointment);
        }
        await queryClient.invalidateQueries(['apt-timeline-details']);
      } else if (code === 4) {
        iaRa(error).map((errorMessage) => showAlert({ message: errorMessage, color: 'danger' }));
      }
    },
    onError: (error) => Honeybadger.notify(`Appointment update error: ${error}`)
  });

  const handleAppointmentAlreadyRescheduled = async (rescheduledAppointmentId) => {
    showAlert({
      title: 'Appointment Rescheduled',
      color: 'warning',
      message: 'This appointment was rescheduled by another user, no changes can be made!',
      buttons: [
        {
          text: 'View Rescheduled Appointment',
          onClick: () => {
            handleClose();
            setTimeout(
              () => navigate(`/portal/appointments?appointment_id=${rescheduledAppointmentId}`),
              1000
            );
          },
          color: 'warning'
        }
      ]
    });
    return;
  };

  const handleAppointmentUpdated = async (updatedAppointment) => {
    queryClient.setQueryData(['appointmentv2', updatedAppointment?.id], (oldData) => {
      return {
        ...oldData,
        appointment: updatedAppointment
      };
    });

    if (updatedAppointment.location) {
      queryClient.invalidateQueries({ queryKey: ['claim'], refetchType: 'all' });
    }
    showAlert({ color: 'success', message: 'Appointment updated successfully!' });
  };

  const handleAppointmentUpdatedAndRescheduled = async (updatedAppointment) => {
    // When we rescheduling an appointment
    handleClose();
    showAlert({
      message: 'This appointment rescheduled successfully!',
      type: 'success',
      buttons: [
        {
          text: 'View',
          onClick: () => navigate(`/portal/appointments?appointment_id=${updatedAppointment?.id}`)
        }
      ]
    });
  };

  const handleAppointmentEntityCreate = async (updatedAppointment) => {
    showAlert({ color: 'success', message: 'Appointment updated successfully!' });
    setAppointmentId(updatedAppointment?.id);
  };

  return {
    mutateAppointment
  };
};

export default useAppointmentUpdate;
